import { sortBy } from 'lodash';
import { FormEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { Checkbox, Radio, RadioGroup, Select, Textarea } from '@electricjs/arc';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useEmployeeDirectory } from '@/hooks/useEmployeeDirectory';
import { useGetOrganizationApplicationsQuery } from '@/redux/slices/organizationApiSlice';

import ApplicationSelect from '../ApplicationSelect/ApplicationSelect';
import { EmployeeStatusEnum } from '@electricjs/core_entity-client';
import { getBackendTranslationKey } from '../../utils/backendTranslations';
import { getDisplayFullName } from 'common/utils/getFullName';

type RequestSupportFormInputProps = {
  name: string;
  type: string;
  // The form fields come from the DB, so we can't statically type them
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any[] | null;
  values?: string[];
  handleCheckbox?: (e: FormEvent) => void;
};

const RequestSupportFormInput = ({
  name,
  type,
  options,
  values,
  handleCheckbox,
  ...field
}: RequestSupportFormInputProps) => {
  const { t } = useTranslation('support-center');
  const organizationId = useGetOrganizationId();

  const { data: activeEmployees, isFetching: employeesFetching } =
    useEmployeeDirectory(
      organizationId && type === 'employeeDropdown'
        ? { organizationId, status: EmployeeStatusEnum.Active }
        : skipToken
    );

  const { data: orgAppsResponse, isFetching: applicationsFetching } =
    useGetOrganizationApplicationsQuery(
      { organizationId },
      {
        skip: !organizationId || type !== 'applicationDropdown',
      }
    );

  const applicationOptions = useMemo(() => {
    const organizationApplications = orgAppsResponse?.applications ?? [];

    return organizationApplications?.map(appData => ({
      name: appData.application.name,
      id: appData.application.id,
      icon: appData.application.icon,
    }));
  }, [orgAppsResponse]);

  switch (type) {
    case 'textarea':
      return (
        <Textarea
          id="request-form-textarea"
          {...field}
          aria-labelledby={name}
        />
      );
    case 'dropdown':
      if (!options) return null;
      return (
        <Select
          id="request-form-select"
          {...field}
          isClearable
          aria-labelledby={name}
          options={options}
          placeholder={t('requestSupportPage.fields.selectPlaceholder')}
          getOptionLabel={option =>
            t(
              getBackendTranslationKey(
                'settings',
                `${name}.options`,
                option.value
              ),
              { defaultValue: option.label }
            )
          }
        />
      );
    case 'employeeDropdown':
      return (
        <Select
          id="request-form-employee-select"
          {...field}
          options={sortBy(activeEmployees, 'firstName')}
          aria-labelledby={name}
          isLoading={employeesFetching}
          loadingText={t('requestSupportForm.input.loadingEmployees')}
          placeholder={t('requestSupportPage.fields.selectPlaceholder')}
          getOptionLabel={employee =>
            `${getDisplayFullName(employee)} (${employee.email})`
          }
          getOptionValue={employee => employee.id}
          isSearchable
          isClearable
        />
      );
    case 'applicationDropdown':
      return (
        <ApplicationSelect
          {...field}
          aria-labelledby={name}
          options={applicationOptions}
          isLoading={applicationsFetching}
          placeholder={t('requestSupportPage.fields.selectPlaceholder')}
        />
      );
    case 'radio': {
      // Arc RadioGroup is Reakit-based and uses `state`/`setState` for controlled
      // selection, not the standard `value`/`onChange`. We remap here so that
      // both programmatic pre-fill (defaultValue via Controller) and user clicks
      // correctly update React Hook Form's state.

      // The `...field` rest has no static type for value/onChange; cast needed
      const {
        value: radioValue,
        onChange: radioOnChange,
        ...radioRest
      } = field as { value: unknown; onChange: unknown; [k: string]: unknown };
      return (
        <RadioGroup
          {...radioRest}
          state={radioValue}
          onChange={radioOnChange}
          aria-label={name}
          stack>
          {options?.map(item => (
            <Radio
              id={`request-form-radio-${String(item)}`}
              key={String(item)}
              value={item}>
              {t(
                getBackendTranslationKey('settings', `${name}.options`, item),
                { defaultValue: item }
              )}
            </Radio>
          ))}
        </RadioGroup>
      );
    }
    case 'checkbox':
      return (
        <>
          {options?.map(item => (
            <Checkbox
              id={`request-form-checkbox-${item.value ?? item}`}
              {...field}
              key={item.value ?? item}
              value={item.value}
              checked={values?.includes(item.value)}
              onChange={handleCheckbox}>
              {t(
                getBackendTranslationKey(
                  'settings',
                  `${name}.options`,
                  item.value
                ),
                { defaultValue: item.label }
              )}
            </Checkbox>
          ))}
        </>
      );
    default:
      return null;
  }
};

export default RequestSupportFormInput;
